import React from 'react';
import { Drawer, Divider } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigation } from './hooks/useNavigation';
import { NAVIGATION_STYLES } from '../../config/constants';
import {
  NavigationHeader,
  NavigationList,
  CollapsedNavigation,
} from './components';

/**
 * Navigation Component
 *
 * Main navigation component that renders either an expanded or collapsed navigation drawer.
 * Uses modular components for better maintainability and follows the same pattern as PromptManagerDashboard.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the navigation is expanded
 * @param {Function} props.onToggle - Callback for toggling navigation state
 */
const Navigation = ({ open, onToggle }) => {
  const { tenant } = useAuth();
  const { handleItemClick, handleSubItemClick, isActive, isExpanded } =
    useNavigation();

  return (
    <>
      {/* Expanded Navigation Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: open ? NAVIGATION_STYLES.drawer.expanded.width : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: NAVIGATION_STYLES.drawer.expanded.width,
            boxSizing: 'border-box',
            backgroundColor: NAVIGATION_STYLES.drawer.expanded.backgroundColor,
            borderRight: NAVIGATION_STYLES.drawer.expanded.borderRight,
          },
        }}
      >
        <NavigationHeader tenant={tenant} onToggle={onToggle} />
        <Divider />
        <NavigationList
          isActive={isActive}
          isExpanded={isExpanded}
          showDescription={open}
          onItemClick={handleItemClick}
          onSubItemClick={handleSubItemClick}
        />
      </Drawer>

      {/* Collapsed Navigation Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={!open}
        sx={{
          width: !open ? NAVIGATION_STYLES.drawer.collapsed.width : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: NAVIGATION_STYLES.drawer.collapsed.width,
            boxSizing: 'border-box',
            backgroundColor: NAVIGATION_STYLES.drawer.collapsed.backgroundColor,
            borderRight: NAVIGATION_STYLES.drawer.collapsed.borderRight,
            overflow: NAVIGATION_STYLES.drawer.collapsed.overflow,
          },
        }}
      >
        <CollapsedNavigation
          onToggle={onToggle}
          onItemClick={handleItemClick}
          isActive={isActive}
        />
      </Drawer>
    </>
  );
};

export default Navigation;
